import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore';

// Helper to get user-scoped collection reference
const getUserCollection = (userId, collectionName) => {
  if (!userId) throw new Error('userId is required');
  return collection(db, 'users', userId, collectionName);
};

/**
 * Create a new charge record
 * @param {string} userId - User ID
 * @param {Object} data - Charge data
 * @param {string} data.patronName - Name of the patron
 * @param {string} data.patronType - 'student' | 'parent' | 'faculty'
 * @param {Array} data.books - Array of {title, barcode, cost, dueDate}
 * @param {number} data.totalCost - Total charge amount
 * @param {string} [data.notes] - Optional notes
 * @returns {Promise<string>} - The new charge document ID
 */
export async function createCharge(userId, data) {
  const chargeData = {
    patronName: data.patronName,
    patronType: data.patronType || 'unknown',
    books: data.books || [],
    totalCost: data.totalCost || 0,
    chargeDate: Timestamp.now(),
    status: 'pending',
    paidDate: null,
    notes: data.notes || '',
    createdAt: Timestamp.now()
  };

  const docRef = await addDoc(getUserCollection(userId, 'charges'), chargeData);
  return docRef.id;
}

/**
 * Update a charge's status
 * @param {string} userId - User ID
 * @param {string} id - Charge document ID
 * @param {string} status - 'pending' | 'paid' | 'waived'
 * @param {Date} [paidDate] - Date of payment (defaults to now if status is 'paid' or 'waived')
 */
export async function updateChargeStatus(userId, id, status, paidDate = null) {
  if (!userId) throw new Error('userId is required');
  const chargeRef = doc(db, 'users', userId, 'charges', id);
  const updateData = {
    status,
    updatedAt: Timestamp.now()
  };

  if (status === 'paid' || status === 'waived') {
    updateData.paidDate = paidDate ? Timestamp.fromDate(new Date(paidDate)) : Timestamp.now();
  }

  await updateDoc(chargeRef, updateData);
}

/**
 * Get all pending charges
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of pending charge objects
 */
export async function getPendingCharges(userId) {
  const q = query(
    getUserCollection(userId, 'charges'),
    where('status', '==', 'pending'),
    orderBy('chargeDate', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    chargeDate: doc.data().chargeDate?.toDate(),
    paidDate: doc.data().paidDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Get all charges (optionally filtered by status)
 * @param {string} userId - User ID
 * @param {string} [status] - Optional status filter
 * @returns {Promise<Array>} - Array of charge objects
 */
export async function getAllCharges(userId, status = null) {
  let q;
  if (status) {
    q = query(
      getUserCollection(userId, 'charges'),
      where('status', '==', status),
      orderBy('chargeDate', 'desc')
    );
  } else {
    q = query(getUserCollection(userId, 'charges'), orderBy('chargeDate', 'desc'));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    chargeDate: doc.data().chargeDate?.toDate(),
    paidDate: doc.data().paidDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Get charge history for a specific patron
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @returns {Promise<Array>} - Array of charge objects for this patron
 */
export async function getChargesByPatron(userId, patronName) {
  const q = query(
    getUserCollection(userId, 'charges'),
    where('patronName', '==', patronName),
    orderBy('chargeDate', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    chargeDate: doc.data().chargeDate?.toDate(),
    paidDate: doc.data().paidDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Check if a patron has a pending charge
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @returns {Promise<boolean>} - True if patron has pending charge
 */
export async function hasBeenCharged(userId, patronName) {
  const q = query(
    getUserCollection(userId, 'charges'),
    where('patronName', '==', patronName),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Record that a notice was sent
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @param {string} patronType - 'student' | 'parent' | 'faculty'
 * @param {string} noticeType - 'initial' | 'final' | 'charge'
 * @param {Array} books - Array of book info sent in the notice
 * @returns {Promise<string>} - The new notice history document ID
 */
export async function recordNoticeSent(userId, patronName, patronType, noticeType, books) {
  const noticeData = {
    patronName,
    patronType: patronType || 'unknown',
    noticeType,
    dateSent: Timestamp.now(),
    books: books || [],
    createdAt: Timestamp.now()
  };

  const docRef = await addDoc(getUserCollection(userId, 'noticeHistory'), noticeData);
  return docRef.id;
}

/**
 * Get all notices sent to a patron
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @returns {Promise<Array>} - Array of notice history objects
 */
export async function getNoticeHistory(userId, patronName) {
  const q = query(
    getUserCollection(userId, 'noticeHistory'),
    where('patronName', '==', patronName),
    orderBy('dateSent', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    dateSent: doc.data().dateSent?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Check if a patron has received a final notice
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @returns {Promise<boolean>} - True if patron has received a final notice
 */
export async function hasReceivedFinalNotice(userId, patronName) {
  const q = query(
    getUserCollection(userId, 'noticeHistory'),
    where('patronName', '==', patronName),
    where('noticeType', '==', 'final')
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Clear all notice history for a patron (used when payment is received)
 * @param {string} userId - User ID
 * @param {string} patronName - Patron's name
 * @returns {Promise<number>} - Number of notices deleted
 */
export async function clearNoticeHistory(userId, patronName) {
  const q = query(
    getUserCollection(userId, 'noticeHistory'),
    where('patronName', '==', patronName)
  );

  const snapshot = await getDocs(q);
  const { deleteDoc: firestoreDeleteDoc } = await import('firebase/firestore');

  const deletePromises = snapshot.docs.map(doc => firestoreDeleteDoc(doc.ref));
  await Promise.all(deletePromises);

  return snapshot.docs.length;
}

/**
 * Get charges within a date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start of date range
 * @param {Date} endDate - End of date range
 * @param {string} [status] - Optional status filter
 * @returns {Promise<Array>} - Array of charge objects
 */
export async function getChargesByDateRange(userId, startDate, endDate, status = null) {
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);

  let q;
  if (status) {
    q = query(
      getUserCollection(userId, 'charges'),
      where('chargeDate', '>=', startTimestamp),
      where('chargeDate', '<=', endTimestamp),
      where('status', '==', status),
      orderBy('chargeDate', 'desc')
    );
  } else {
    q = query(
      getUserCollection(userId, 'charges'),
      where('chargeDate', '>=', startTimestamp),
      where('chargeDate', '<=', endTimestamp),
      orderBy('chargeDate', 'desc')
    );
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    chargeDate: doc.data().chargeDate?.toDate(),
    paidDate: doc.data().paidDate?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Get summary statistics for charges
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Summary stats
 */
export async function getChargeStats(userId) {
  const allCharges = await getAllCharges(userId);

  const stats = {
    totalPending: 0,
    totalPaid: 0,
    totalWaived: 0,
    pendingAmount: 0,
    paidAmount: 0,
    waivedAmount: 0,
    totalCharges: allCharges.length
  };

  allCharges.forEach(charge => {
    const amount = charge.totalCost || 0;
    if (charge.status === 'pending') {
      stats.totalPending++;
      stats.pendingAmount += amount;
    } else if (charge.status === 'paid') {
      stats.totalPaid++;
      stats.paidAmount += amount;
    } else if (charge.status === 'waived') {
      stats.totalWaived++;
      stats.waivedAmount += amount;
    }
  });

  return stats;
}

/**
 * Delete a charge record
 * @param {string} userId - User ID
 * @param {string} id - Charge document ID
 */
export async function deleteCharge(userId, id) {
  if (!userId) throw new Error('userId is required');
  const { deleteDoc: firestoreDeleteDoc } = await import('firebase/firestore');
  const chargeRef = doc(db, 'users', userId, 'charges', id);
  await firestoreDeleteDoc(chargeRef);
}

/**
 * Update charge notes
 * @param {string} userId - User ID
 * @param {string} id - Charge document ID
 * @param {string} notes - New notes content
 */
export async function updateChargeNotes(userId, id, notes) {
  if (!userId) throw new Error('userId is required');
  const chargeRef = doc(db, 'users', userId, 'charges', id);
  await updateDoc(chargeRef, {
    notes,
    updatedAt: Timestamp.now()
  });
}

/**
 * Get all notice history (for searching/browsing)
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of all notice history objects
 */
export async function getAllNoticeHistory(userId) {
  const q = query(
    getUserCollection(userId, 'noticeHistory'),
    orderBy('dateSent', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    dateSent: doc.data().dateSent?.toDate(),
    createdAt: doc.data().createdAt?.toDate()
  }));
}

/**
 * Get unique patron names from notice history (for search/autocomplete)
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of unique patron names with their notice counts
 */
export async function getPatronsWithNotices(userId) {
  const allNotices = await getAllNoticeHistory(userId);

  // Group by patron name
  const patronMap = new Map();
  allNotices.forEach(notice => {
    const existing = patronMap.get(notice.patronName) || { count: 0, types: new Set() };
    existing.count++;
    existing.types.add(notice.noticeType);
    patronMap.set(notice.patronName, existing);
  });

  // Convert to array
  return Array.from(patronMap.entries()).map(([name, data]) => ({
    patronName: name,
    noticeCount: data.count,
    noticeTypes: Array.from(data.types)
  })).sort((a, b) => a.patronName.localeCompare(b.patronName));
}
